// src/components/UserFiles/UserFilesList.jsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  Pagination,
  Chip,
  Stack,
} from '@mui/material';

import { useSnackbar } from 'notistack';

import { downloadFile, deleteFile } from '../../../services/userFileService';
import UserFileSignDialog from './UserFileSignDialog';
import UserFileSignaturesDialog from './UserFileSignaturesDialog';

import UserFilesListHeader from './UserFilesListHeader';
import UserFileRow from './UserFileRow';
import UserFileGroupCard from './UserFileGroupCard';
import { useGroupedFiles } from './useGroupedFiles';
import { InsertDriveFileOutlined } from '@mui/icons-material';

const UserFilesList = ({
  user,
  t,
  files,
  loadingFiles,
  onFilesChanged,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  isSearching,
  searchQuery,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [fileToSign, setFileToSign] = useState(null);
  const [signaturesDialogOpen, setSignaturesDialogOpen] = useState(false);
  const [fileForSignatures, setFileForSignatures] = useState(null);

  const groupedFiles = useGroupedFiles(files);

  const handleDownload = async (file) => {
    const res = await downloadFile(file);
    if (!res.success) {
      const msg =
        res.error || t('files.download_failed', 'Nepodarilo sa stiahnuť súbor.');
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm(t('files.delete_confirm', 'Naozaj chcete odstrániť tento súbor?'))) {
      return;
    }

    try {
      const res = await deleteFile(fileId);
      if (res.success) {
        enqueueSnackbar(t('files.delete_success', 'Súbor bol odstránený.'), {
          variant: 'success',
        });
        if (onFilesChanged) await onFilesChanged();
      } else {
        const msg =
          res.error || t('files.delete_failed', 'Nepodarilo sa odstrániť súbor.');
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } catch (e) {
      console.error('Delete error', e);
      enqueueSnackbar(t('files.delete_failed', 'Nepodarilo sa odstrániť súbor.'), {
        variant: 'error',
      });
    }
  };

  const handleOpenSignDialog = (file) => {
    const isOverdue = !!file.is_overdue;

    if (isOverdue) {
      enqueueSnackbar(t('files.deadline_passed', 'Termín na podpis tohto súboru už uplynul.'), {
        variant: 'error',
      });
      return;
    }

    if (!user?.is_2fa_enabled) {
      enqueueSnackbar(
        t(
          'files.2fa_required_for_sign',
          'Na podpis súborov musíte mať najprv zapnuté dvojfaktorové overenie (2FA).'
        ),
        { variant: 'warning' }
      );
      return;
    }

    setFileToSign(file);
    setSignDialogOpen(true);
  };

  const handleCloseSignDialog = () => {
    setSignDialogOpen(false);
    setFileToSign(null);
  };

  const handleSigned = async () => {
    if (onFilesChanged) await onFilesChanged();
  };

  const handleOpenSignaturesDialog = (file) => {
    setFileForSignatures(file);
    setSignaturesDialogOpen(true);
  };

  const handleCloseSignaturesDialog = () => {
    setSignaturesDialogOpen(false);
    setFileForSignatures(null);
  };

  if (loadingFiles) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, mt: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalPages = pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;

  const resultsLabel = (() => {
    if (!totalCount) {
      return isSearching
        ? t('files.no_results', 'Nenašli sa žiadne výsledky.')
        : t('files.no_files', 'Zatiaľ ste nenahrali žiadne súbory.');
    }

    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, totalCount);

    return isSearching
      ? t('files.showing_results', 'Zobrazené {{start}}-{{end}} z {{total}} výsledkov', {
          start,
          end,
          total: totalCount,
        })
      : t('files.showing_files', 'Zobrazené {{start}}-{{end}} z {{total}} súborov', {
          start,
          end,
          total: totalCount,
        });
  })();

  const chipStyle = { borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem' };

  return (
    <>
      {/* Toolbar: left title, middle chips, right page-size */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'auto 1fr auto' },
          alignItems: 'center',
          gap: 2,
          mb: 2,
          p: 1.5,
          bgcolor: 'background.default',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Left */}
        <Typography variant="subtitle1" sx={{ m: 0, fontWeight: 700, color: 'text.primary' }}>
          {t('files.your_files', 'Vaše súbory')}
        </Typography>

        {/* Middle */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent={{ xs: 'flex-start', md: 'center' }}
          flexWrap="wrap"
          sx={{ minWidth: 0 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
            {resultsLabel}
          </Typography>

          {isSearching && searchQuery && (
            <Chip
              size="small"
              variant="outlined"
              sx={chipStyle}
              label={t('files.search_chip', 'Hľadám: "{{q}}"', { q: searchQuery })}
            />
          )}

          <Chip
            size="small"
            color={totalCount > 0 ? 'primary' : 'default'}
            sx={{ ...chipStyle, ...(totalCount === 0 && { bgcolor: 'transparent', borderColor: 'divider' }) }}
            label={t('files.total', 'Spolu: {{n}}', { n: totalCount || 0 })}
          />
        </Stack>

        {/* Right */}
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <UserFilesListHeader
            t={t}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            compact
            hideTitle
          />
        </Box>
      </Box>

      {/* Empty state */}
      {(!files || files.length === 0) ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            p: 4, 
            minHeight: 250, 
            bgcolor: 'background.default', 
            borderRadius: 3,
            border: '1px dashed', 
            borderColor: 'divider',
            mt: 2
          }}
        >
          <InsertDriveFileOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600 }} gutterBottom>
             {isSearching ? t('files.no_results_title', 'Žiadne výsledky') : t('files.no_files_title', 'Prázdny zoznam')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
            {isSearching
              ? t('files.no_results', 'Nenašli sa žiadne výsledky pre váš hľadaný výraz. Skúste zmeniť text vyhľadávania.')
              : t('files.no_files', 'Zatiaľ ste nenahrali žiadne súbory. Použite sekciu vyššie pre nahranie prvého dokumentu.')}
          </Typography>
        </Box>
      ) : (
        <>
          <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {groupedFiles.map((group) => {
              if (group.isRealGroup && group.files.length > 1) {
                return (
                  <UserFileGroupCard
                    key={group.id}
                    group={group}
                    t={t}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    onOpenSignDialog={handleOpenSignDialog}
                    onOpenSignaturesDialog={handleOpenSignaturesDialog}
                  />
                );
              }

              const file = group.files[0];
              return (
                <UserFileRow
                  key={`${group.id}-single-${file.id}`}
                  file={file}
                  t={t}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  onOpenSignDialog={handleOpenSignDialog}
                  onOpenSignaturesDialog={handleOpenSignaturesDialog}
                  withDivider={false} // Adjusting this because we're using gap in the list now
                />
              );
            })}
          </List>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                page={page}
                count={totalPages}
                color="primary"
                shape="rounded"
                onChange={(_, value) => onPageChange?.(value)}
              />
            </Box>
          )}
        </>
      )}

      <UserFileSignDialog
        open={signDialogOpen}
        onClose={handleCloseSignDialog}
        file={fileToSign}
        t={t}
        onSigned={handleSigned}
      />

      <UserFileSignaturesDialog
        open={signaturesDialogOpen}
        onClose={handleCloseSignaturesDialog}
        file={fileForSignatures}
        t={t}
      />
    </>
  );
};

export default UserFilesList;